import { OperationConfigurations } from 'core/api'
import { RawSchemaField } from 'stylewhere/shared'
import { OperationUserSetting } from 'stylewhere/types'
import { showToast } from 'stylewhere/utils'
import { navigate } from './router'

type ConfirmBehavior = 'no' | 'yes' | 'withWarning'
type TrueFalse = 'true' | 'false'

interface BaseOperationConfig {
  type:
    | 'TRANSFER_TO_ZONE'
    | 'SIMPLE_ITEM_LIST'
    | 'SIMPLE_PARCEL_LIST'
    | 'INBOUND'
    | 'OUTBOUND'
    | 'PACKING'
    | 'SORTING'
    | 'ENCODING'
    | 'INVENTORY'
    | 'TAG_INFO'
    | 'SIMPLE_ITEM_SESSION_LIST'
    | 'SIMPLE_READING_LIST'
  id: string
  creationDate: Date
  lastModifyDate: Date
  code: string
  description: string
  displayPriority: number
  formSchema?: RawSchemaField[]
  enabled: boolean
  clients: string[]
  actions: any[]
  attributes: {
    key: string
    attr: string
  }
}

export interface RawOperationConfig extends BaseOperationConfig {
  options: {
    customExtensionCode: string
    errorStates: string
    ignoreStates: string
    warningStates: string
    ignoreWithReasonStates: string
    errorLabels: string
    ignoreLabels: string
    warningLabels: string
    readingMode: 'rfid' | 'mixed' | 'barcode'
    shippingMode: 'parcelByParcel' | 'multiParcel' | 'shipment'
    canConfirmWithMissing: ConfirmBehavior
    canConfirmWithOverQuantity: ConfirmBehavior
    canConfirmWithUnexpected: ConfirmBehavior
    createChecklistOnConfirm: TrueFalse
    hasChecklist: TrueFalse
    showModalOnConfirm?: TrueFalse
    persistParcelContentScannings: 'onUserConfirm' | 'onBatchRead'
    hasMovement: TrueFalse
    autostartAntenna: TrueFalse
    autostopAntennaTimeout: number | undefined
    avoidRestartingTimerOnRead: TrueFalse
    startWithRfidSearch: TrueFalse
    confirmOnItemRead: TrueFalse
    readingsDisplayMode: 'groupedByProduct' | 'item'
    'stw.core.logistics.commons.config.requiredparcelcode': TrueFalse
    enableOriginZone: TrueFalse
    enablePicking: TrueFalse
    showDeleteButton: TrueFalse
    showCloseButton: TrueFalse
    showCreateButton: TrueFalse
    pricingEnabled: TrueFalse
    refreshCounterTimeout: number | undefined
    formSchemaSingleRow?: boolean | string
    postConfirmAction: string | undefined
    disableWorkstationCheck?: boolean | string
    activeStorageForm?: boolean | string
    enabledCheckItemZone?: boolean | string
    canForceAssociation: string | undefined
    scanIdentifier: 'rfid' | 'rfidAndSerial' | 'serial' | undefined
    forcePin: string | undefined
    forceRetries: number | undefined
    batchInterval: TrueFalse
    batchIntervalTime?: number | string
    batchIntervalTagCount?: number | string
    templatePath: string | undefined
    showSessionWarningIfNoQc?: boolean | string
    blockListIfNoQc?: boolean | string
    sessionRework: string | undefined
    sessionUpdatePolicy: string | undefined
    resetReadingsAfterOperation: number | string | undefined
    stopAndResetAntennaTimeout: number | string | undefined
    executionModeForSyncConfirmation: 'sync' | 'async' | undefined
    skipValidation: boolean | undefined | string
    expectOnlyMandatoryIdentifiers: boolean | undefined | string
    enableUhfWrite: 'no' | 'gs1' | 'custom' | undefined
    askUserInputEveryTime?: TrueFalse
    antennaTurnWaitingInterval: number | undefined
    automaticRestartAntenna?: TrueFalse
    errorMessageView: 'toast' | 'modal' | undefined
    newReadingsFirst: string | undefined
    saveOnReadActionCode: string | undefined
    startAsnScreen: undefined | 'asnList' | 'inputCode' | 'asnListAndInputCode' | 'no'
    showListOnStart?: TrueFalse
    canCreateParcels?: TrueFalse
    canCreateShipments?: TrueFalse
    canDeleteParcel?: TrueFalse
    removeMode: undefined | 'sku' | 'rfid' | 'identifier' | 'none'
    genericDiscrepancyMessage?: TrueFalse
    reverseAsnOrder?: TrueFalse
    reverseParcelOrder?: TrueFalse
    allowUnpack?: TrueFalse | 'trueWithContentRetrieval'
    shippingReadTimeout?: number
    enableCheckZone?: TrueFalse
    showTransactionDateModal?: TrueFalse
    viewMode?: string
    filterByOperation?: TrueFalse
    filterForSpecificOperations?: string
    filterByPlace?: TrueFalse
    showAlertOnExit?: TrueFalse
    startParcelWithAsn?: TrueFalse
    userSettings?: string
    validateEncodingOperationCode?: string
    scanningContentMode?: 'massive' | 'itemByItem'
    targetEntity: 'NONE' | 'identifiers' | 'identifierReels' | 'items' | 'products'
    checkTagAfterWrite?: TrueFalse
    checkTagAfterKill?: TrueFalse
    checklistMode?: 'parcel' | 'shipment'
    askToCloseShipmentOnLastParcel?: TrueFalse | 'askUserConfirmation' | 'no' | 'forceCloseAndInformUser'
    userManualFilename?: string
    showOnlyVerify?: TrueFalse
    itemCreationMode?: 'sync' | 'async'
    showAdvancedPoCounters?: TrueFalse
  }
}

export interface OperationConfigCore extends BaseOperationConfig {
  itemStatuses: {
    error: string[]
    warning: string[]
    ignore: string[]
    ignoreWithReason: string[]
  }
  itemLabels: {
    error: string[]
    warning: string[]
    ignore: string[]
  }
  autostartAntenna: boolean
  autostopAntennaTimeout: number
  avoidRestartingTimerOnRead: boolean
  refreshCounterTimeout: number
  formSchemaSingleRow: boolean
  postConfirmAction: string
  activeStorageForm: boolean
  enabledCheckItemZone: boolean
  disableWorkstationCheck: boolean
  readingsDisplayMode: 'groupedByProduct' | 'item'
  removeMode: 'sku' | 'rfid' | 'identifier' | 'none'
  batchInterval: boolean
  batchIntervalTime: number
  batchIntervalTagCount: number
  templatePath: string
  options: any
  executionModeForSyncConfirmation: 'sync' | 'async'
  errorMessageView: 'toast' | 'modal'
  newReadingsFirst: boolean
  saveOnReadActionCode: string | undefined
  showAlertOnExit: boolean
  userSettings: OperationUserSetting[]
  userManualFilename: string | undefined
}

export interface TransferToZoneOperationConfig extends OperationConfigCore {
  type: 'TRANSFER_TO_ZONE'
}

export interface SimpleItemListOperationConfig extends OperationConfigCore {
  type: 'SIMPLE_ITEM_LIST'
  customExtensionCode: string
  confirmOnItemRead: boolean
}

export interface SimpleReadingListOperationConfig extends OperationConfigCore {
  type: 'SIMPLE_READING_LIST'
  customExtensionCode: string
  hasChecklist: boolean
  showListOnStart: boolean
  readingMode: string
  targetEntity: 'NONE' | 'identifiers' | 'identifierReels' | 'items' | 'products'
}

export interface SimpleParcelListOperationConfig extends OperationConfigCore {
  type: 'SIMPLE_PARCEL_LIST'
  viewMode: string
  showModalOnConfirm: boolean
}

export interface ShippingOperationConfig extends OperationConfigCore {
  type: 'INBOUND' | 'OUTBOUND' | 'PACKING'
  hasChecklist: boolean
  readingMode: 'rfid' | 'mixed' | 'barcode'
  shippingMode: 'parcelByParcel' | 'multiParcel' | 'shipment'
  canConfirmWithMissing: ConfirmBehavior
  canConfirmWithOverQuantity: ConfirmBehavior
  canConfirmWithUnexpected: ConfirmBehavior
  requiredParcelCode?: boolean
  startWithRfidSearch: boolean
  showModalOnConfirm: boolean
  persistParcelContentScannings: 'onUserConfirm' | 'onBatchRead'
  showListOnStart: boolean
  canCreateParcels: boolean
  canCreateShipments: boolean
  canDeleteParcel: boolean
  removeMode: 'none' | 'sku' | 'identifier' | 'rfid'
  genericDiscrepancyMessage: boolean
  reverseAsnOrder: boolean
  reverseParcelOrder: boolean
  allowUnpack?: TrueFalse | 'trueWithContentRetrieval'
  shippingReadTimeout?: number
  filterByOperation?: boolean
  filterForSpecificOperations?: string
  startParcelWithAsn?: boolean
  validateEncodingOperationCode?: string
  scanningContentMode: 'massive' | 'itemByItem'
  filterByPlace?: boolean
  checklistMode?: 'parcel' | 'shipment'
  askToCloseShipmentOnLastParcel?: TrueFalse | 'askUserConfirmation' | 'no' | 'forceCloseAndInformUser'
}

export interface InboundOperationConfig extends ShippingOperationConfig {
  type: 'INBOUND'
}

export interface OutboundOperationConfig extends ShippingOperationConfig {
  type: 'OUTBOUND'
}

export interface PackingOperationConfig extends ShippingOperationConfig {
  type: 'PACKING'
}

export interface SortingOperationConfig extends OperationConfigCore {
  type: 'SORTING'
  enableOriginZone: boolean
  enablePicking: boolean
  showCreateButton: boolean
  showCloseButton: boolean
  showDeleteButton: boolean
  pricingEnabled: boolean
  itemConfirmMode?: string
  enableCheckZone?: boolean
  showTransactionDateModal?: boolean
}

export interface EncodingOperationConfig extends OperationConfigCore {
  type: 'ENCODING'
  options: {
    initialType: 'order' | 'product' | 'identifier' | 'wam' | 'formSchema'
    canForceAssociation: string
    scanIdentifier?: 'rfid' | 'rfidAndSerial' | 'serial'
    forcePin: string
    forceRetries: number
    productionOrderRowSelectionMode: 'byOrderAndRowCode' | 'byOrderAndSku' | 'byCode'
    timeoutValidateTags?: number
    timeoutTagsNoReads?: number
    resetReadingsAfterOperation?: number
    skipValidation: boolean
    expectOnlyMandatoryIdentifiers: boolean
    enableUhfWrite: 'no' | 'gs1' | 'custom'
    askUserInputEveryTime: TrueFalse
    antennaTurnWaitingInterval: number | undefined
    automaticRestartAntenna?: TrueFalse
    encodingTagPassword: string | undefined
    encodingKillTagPassword: string | undefined
    ignoreReadingTagsWithPrefixes: string | undefined
    useTidTargetDuringWrite?: TrueFalse
    onItemAlreadyEncodedFeedback?: 'confirmModal' | 'infoToast'
    forceTagRewrite?: TrueFalse
    ignoreUnknownTags?: 'yes' | 'no'
    startAntennaOrder?: 'all' | 'nfc_uhf' | 'uhf_nfc'
    checkWorkerOnProductionOrder?: 'yes' | 'no' | 'userPlaces'
    encodingKillTagMode?: 'uhf_nfc' | 'only_uhf' | 'only_nfc' | 'none'
    checkTagAfterWrite?: TrueFalse
    checkTagAfterKill?: TrueFalse
    canEncodeWithOverQuantity: 'yes' | 'no' | undefined
    checkEncodedQuantity?: TrueFalse
    showOnlyVerify?: TrueFalse
    disableAutomaticStart?: TrueFalse
    itemCreationMode?: 'sync' | 'async'
    showAdvancedPoCounters?: TrueFalse
  }
}

export interface InventoryOperationConfig extends OperationConfigCore {
  type: 'INVENTORY'
}

export interface TagInfoOperationConfig extends OperationConfigCore {
  type: 'TAG_INFO'
}

export interface SessionItemSessionListOperationConfig extends OperationConfigCore {
  type: 'SIMPLE_ITEM_SESSION_LIST'
  resetReadingsAfterOperation: number
  stopAndResetAntennaTimeout: number
}

export type OperationConfig =
  | TransferToZoneOperationConfig
  | SimpleItemListOperationConfig
  | SimpleParcelListOperationConfig
  | InboundOperationConfig
  | OutboundOperationConfig
  | PackingOperationConfig
  | ShippingOperationConfig
  | SortingOperationConfig
  | EncodingOperationConfig
  | InventoryOperationConfig
  | TagInfoOperationConfig
  | SessionItemSessionListOperationConfig
  | SimpleReadingListOperationConfig

export class RemoteOperationBase {
  operations: OperationConfig[] = []

  async load(): Promise<void> {
    try {
      const rawOperations = await OperationConfigurations.search<RawOperationConfig>({
        size: 10000,
        clients: 'station',
        enabled: true,
        showUserOperations: true
      })
      if (rawOperations?.content) {
        this.operations = rawOperations.content.map((op) => mapOperation(op))
      }
    } catch (error) {
      console.error('Error loading operations', error)
    }
  }

  getOperationConfig<T extends OperationConfig = OperationConfig>(code: string): T {
    const operation = this.operations.find((o) => o.code === code)
    if (!operation) {
      showToast({ status: 'error', description: `Operation ${code} not found.` })
      navigate('/')
    }
    return operation as T
  }

  getOperationConfigById<T extends OperationConfig = OperationConfig>(id: string): T {
    const operation = this.operations.find((o) => o.id === id)
    return operation as T
  }
}

export function mapOperation<T extends OperationConfig = OperationConfig>(rawOp: RawOperationConfig): T {
  const { options, ...rawOperation } = rawOp
  const {
    errorStates,
    warningStates,
    ignoreStates,
    ignoreWithReasonStates,
    errorLabels,
    warningLabels,
    ignoreLabels,
    autostartAntenna,
    readingsDisplayMode,
    autostopAntennaTimeout,
    avoidRestartingTimerOnRead,
    removeMode,
    refreshCounterTimeout,
    formSchemaSingleRow,
    postConfirmAction,
    activeStorageForm,
    enabledCheckItemZone,
    disableWorkstationCheck,
    batchInterval,
    batchIntervalTime,
    batchIntervalTagCount,
    templatePath,
    newReadingsFirst,
    saveOnReadActionCode,
    showAlertOnExit,
    userSettings,
    userManualFilename,
    ...restOptions
  } = options
  const base: OperationConfigCore = {
    ...rawOperation,
    itemStatuses: {
      error: errorStates?.split(',') ?? [],
      warning: warningStates?.split(',') ?? [],
      ignore: ignoreStates?.split(',') ?? [],
      ignoreWithReason: ignoreWithReasonStates?.split(',') ?? []
    },
    itemLabels: (() => {
      const parsed = {
        error: errorLabels?.split(',') ?? [],
        warning: warningLabels?.split(',') ?? [],
        ignore: ignoreLabels?.split(',') ?? []
      }
      return parsed
    })(),
    autostartAntenna: autostartAntenna === 'true',
    autostopAntennaTimeout: autostopAntennaTimeout ?? 0,
    avoidRestartingTimerOnRead: !!(avoidRestartingTimerOnRead && avoidRestartingTimerOnRead === 'true'),
    refreshCounterTimeout: refreshCounterTimeout ?? 30000,
    readingsDisplayMode: readingsDisplayMode ?? 'groupedByProduct',
    removeMode: removeMode ?? 'none',
    options: restOptions,
    formSchemaSingleRow: !!(formSchemaSingleRow && formSchemaSingleRow === 'true'),
    postConfirmAction: postConfirmAction ?? 'disabled',
    activeStorageForm: !!(activeStorageForm && activeStorageForm === 'true'),
    enabledCheckItemZone: !!(enabledCheckItemZone && enabledCheckItemZone === 'true'),
    disableWorkstationCheck: !!(disableWorkstationCheck && disableWorkstationCheck === 'true'),
    batchInterval: !!(batchInterval && batchInterval === 'true'),
    batchIntervalTime: batchIntervalTime ? Number.parseInt(batchIntervalTime + '', 10) : 1000,
    batchIntervalTagCount: batchIntervalTagCount ? Number.parseInt(batchIntervalTagCount + '', 10) : 200,
    templatePath: templatePath || '',
    executionModeForSyncConfirmation: options.executionModeForSyncConfirmation || 'sync',
    errorMessageView: options.errorMessageView || 'toast',
    newReadingsFirst: !!(newReadingsFirst && newReadingsFirst === 'true'),
    saveOnReadActionCode: saveOnReadActionCode,
    showAlertOnExit: showAlertOnExit === 'true',
    userSettings: userSettings === undefined ? [] : JSON.parse(userSettings),
    userManualFilename: userManualFilename ?? undefined
  }

  return mapOperationByType(base, options)
}

export default new RemoteOperationBase()

function mapOperationByType<T extends OperationConfig = OperationConfig>(base: OperationConfigCore, options): T {
  switch (base.type) {
    case 'INBOUND':
    case 'OUTBOUND':
    case 'PACKING':
      return getShippingOperation(base, options)
    case 'SIMPLE_ITEM_LIST':
      return getSimpleItemListOperation(base, options)
    case 'SIMPLE_PARCEL_LIST':
      return getSimpleParcelListOperation(base, options)
    case 'SIMPLE_READING_LIST':
      return getSimpleReadingListOperation(base, options)
    case 'SORTING':
      return getSortingOperation(base, options)
    case 'SIMPLE_ITEM_SESSION_LIST':
      return getSimpleItemSessionListOperation(base, options)
    default:
      return { ...base, type: base.type } as T
  }
}

function getSortingOperation<T extends OperationConfig = OperationConfig>(base: OperationConfigCore, options): T {
  return {
    ...base,
    type: base.type,
    enableOriginZone: options.enableOriginZone && options.enableOriginZone === 'true',
    enablePicking: options.enablePicking && options.enablePicking === 'true',
    showCreateButton: options.showCreateButton && options.showCreateButton === 'true',
    showCloseButton: options.showCloseButton && options.showCloseButton === 'true',
    showDeleteButton: options.showDeleteButton && options.showDeleteButton === 'true',
    pricingEnabled: options.pricingEnabled && options.pricingEnabled === 'true',
    enableCheckZone: options.enableCheckZone && options.enableCheckZone === 'true',
    showTransactionDateModal: options.showTransactionDateModal && options.showTransactionDateModal === 'true'
  } as T
}

function getShippingOperationData(options) {
  const startAsnScreen = options.startAsnScreen ?? 'no'
  let showListOnStart = options.showListOnStart === undefined ? false : options.showListOnStart === 'true'
  let canCreateShipments = options.canCreateShipments === undefined ? false : options.canCreateShipments === 'true'
  if (options.canCreateShipments === undefined && ['inputCode', 'asnListAndInputCode'].includes(startAsnScreen))
    canCreateShipments = true
  if (options.showListOnStart === undefined && ['asnList', 'asnListAndInputCode'].includes(startAsnScreen))
    showListOnStart = true

  let askToCloseShipmentOnLastParcel = options.askToCloseShipmentOnLastParcel ?? 'no'
  if (askToCloseShipmentOnLastParcel === 'true') askToCloseShipmentOnLastParcel = 'askUserConfirmation'
  else if (askToCloseShipmentOnLastParcel === 'false') askToCloseShipmentOnLastParcel = 'no'

  return {
    showListOnStart: showListOnStart,
    canCreateShipments: canCreateShipments,
    askToCloseShipmentOnLastParcel: askToCloseShipmentOnLastParcel
  }
}

function getShippingOperation<T extends OperationConfig = OperationConfig>(base: OperationConfigCore, options): T {
  const data = getShippingOperationData(options)
  return {
    ...base,
    type: base.type,
    hasChecklist: options.hasChecklist === 'true',
    readingMode: options.readingMode ?? 'rfid',
    shippingMode: options.shippingMode ?? 'parcelByParcel',
    canConfirmWithMissing: options.canConfirmWithMissing ?? 'withWarning',
    canConfirmWithOverQuantity: options.canConfirmWithOverQuantity ?? 'withWarning',
    canConfirmWithUnexpected: options.canConfirmWithUnexpected ?? 'withWarning',
    startWithRfidSearch: options.startWithRfidSearch === 'true',
    showModalOnConfirm: !!(options.showModalOnConfirm && options.showModalOnConfirm === 'true'),
    persistParcelContentScannings:
      options.persistParcelContentScannings && options.persistParcelContentScannings === 'onBatchRead'
        ? 'onBatchRead'
        : 'onUserConfirm',
    showListOnStart: data.showListOnStart,
    canCreateParcels: options.canCreateParcels === 'true',
    canCreateShipments: data.canCreateShipments,
    canDeleteParcel: options.canDeleteParcel === 'true',
    removeMode: options.removeMode ?? 'none',
    genericDiscrepancyMessage: !!(options.genericDiscrepancyMessage && options.genericDiscrepancyMessage === 'true'),
    reverseAsnOrder: !!(options.reverseAsnOrder && options.reverseAsnOrder === 'true'),
    reverseParcelOrder: !!(options.reverseParcelOrder && options.reverseParcelOrder === 'true'),
    allowUnpack: options.allowUnpack ?? 'false',
    shippingReadTimeout: options.shippingReadTimeout ?? 1000,
    filterByOperation: !!(options.filterByOperation && options.filterByOperation === 'true'),
    filterForSpecificOperations:
      options.filterForSpecificOperations && options.filterByOperation && options.filterByOperation === 'true'
        ? options.filterForSpecificOperations
        : '',
    filterByPlace: !!(
      options.filterByPlace === undefined ||
      (options.filterByPlace !== undefined && options.filterByPlace === 'true')
    ),
    startParcelWithAsn: options.startParcelWithAsn !== 'false',
    validateEncodingOperationCode: options.validateEncodingOperationCode,
    scanningContentMode: options.scanningContentMode ?? 'massive',
    askToCloseShipmentOnLastParcel: data.askToCloseShipmentOnLastParcel,
    templatePath: options.templatePath ?? (options.scanningContentMode === 'itemByItem' ? 'withEncoding' : '')
  } as T
}

function getSimpleItemListOperation<T extends OperationConfig = OperationConfig>(
  base: OperationConfigCore,
  options
): T {
  return {
    ...base,
    type: base.type,
    customExtensionCode: options.customExtensionCode,
    confirmOnItemRead: options.confirmOnItemRead === 'true'
  } as T
}

function getSimpleParcelListOperation<T extends OperationConfig = OperationConfig>(
  base: OperationConfigCore,
  options
): T {
  return {
    ...base,
    type: base.type,
    viewMode: options.viewMode ?? 'parcel',
    showModalOnConfirm: !!(options.showModalOnConfirm && options.showModalOnConfirm === 'true')
  } as T
}

function getSimpleReadingListOperation<T extends OperationConfig = OperationConfig>(
  base: OperationConfigCore,
  options
): T {
  return {
    ...base,
    type: base.type,
    hasChecklist: options.hasChecklist === 'true',
    showListOnStart: options.showListOnStart === 'true',
    readingMode: options.readingMode && options.readingMode !== 'mixed' ? options.readingMode : 'rfid',
    customExtensionCode: options.customExtensionCode,
    targetEntity: options.targetEntity ?? 'NONE'
  } as T
}

function getSimpleItemSessionListOperation<T extends OperationConfig = OperationConfig>(
  base: OperationConfigCore,
  options
): T {
  return {
    ...base,
    type: base.type,
    resetReadingsAfterOperation: options.resetReadingsAfterOperation
      ? Number.parseInt(options.resetReadingsAfterOperation + '', 10)
      : 500,
    stopAndResetAntennaTimeout: options.stopAndResetAntennaTimeout
      ? Number.parseInt(options.stopAndResetAntennaTimeout + '', 10)
      : 3000
  } as T
}
